import type { RefObject } from "react"
import { Fragment, useEffect, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

import type { AgentOrbColors } from "./agent-orb/agent-orb"
import { AgentOrb } from "./agent-orb/agent-orb"
import { AiChatInput } from "./ai-chat-input"
import { Button } from "./button"

export interface HelpCenterGreetings {
	morning: string
	afternoon: string
	evening: string
}

const DEFAULT_GREETINGS: HelpCenterGreetings = {
	morning: "Good morning",
	afternoon: "Good afternoon",
	evening: "Good evening",
}

export function pickGreeting(
	greetings: HelpCenterGreetings,
	hour: number = new Date().getHours(),
) {
	if (hour >= 5 && hour < 12) return greetings.morning
	if (hour >= 12 && hour < 18) return greetings.afternoon
	return greetings.evening
}

// The server renders its own hour, so this rewrites the greeting from the
// reader's clock while the HTML parses, before the first paint.
function preparePaintScript(greetings: HelpCenterGreetings) {
	const labels = JSON.stringify(greetings).replace(/</g, "\\u003c")
	return `(function(){var s=document.currentScript,e=s&&s.previousElementSibling;if(!e||!e.hasAttribute("data-greeting")){var n=document.querySelectorAll("[data-greeting]");e=n[n.length-1]}if(!e)return;var g=${labels},h=new Date().getHours();e.textContent=(h>=5&&h<12?g.morning:h>=12&&h<18?g.afternoon:g.evening)+"."})()`
}

// The tray holds the field and the questions as one block: 14px radius, 4px inset.
// Light rests on the article tiles' own fill and hairline.
// Dark sinks the tray under the page so the field still reads as the raised one.
const TRAY = [
	// -mx-1 pulls the tray out by its own inset, so the field lines up with the column.
	"-mx-1 bg-black/[0.02] p-1 ring-1 ring-black/[0.09] sm:pb-1.5",
	"dark:bg-black/25 dark:shadow-[inset_0_1px_3px_rgba(0,0,0,0.5)] dark:ring-white/[0.05]",
]

// 48px field carrying the wide low-opacity lift at rest, so the bar reads as the
// thing to use before anyone clicks it.
const FIELD = [
	// 9px right gutter: the same air the 30px submit has above and below it.
	"[&_label]:pl-3.5 [&_label]:pr-[9px]",
	// A touch past subtleSurfaceRing, so the edge holds on white. Dark keeps its own ring.
	"[&_label]:ring-black/[0.13] [&_label_button:last-child]:ring-black/[0.13]",
	"dark:[&_label]:bg-gray-850 dark:[&_label]:hover:bg-gray-850",
	"[&_label]:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(15,17,21,0.04),0_12px_26px_-18px_rgba(15,17,21,0.30)]",
	"dark:[&_label]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_1px_2px_rgba(0,0,0,1),0_12px_26px_-18px_rgba(0,0,0,0.7)]",
	"[&_label_button]:h-[30px] [&_label_button]:w-[30px]",
]

// A phone gets full-width 36px rows; from sm the questions run inline, dot-separated.
const QUESTION = [
	// !px-3.5 puts a row's text on the placeholder's left edge.
	"h-9 min-h-0 w-full max-w-none justify-start rounded-md !px-3.5 text-[13px] font-normal tracking-[-0.01em]",
	"text-gray-500 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
	"sm:h-auto sm:w-auto sm:max-w-full sm:justify-center sm:!px-0 sm:text-[12.5px]",
]

interface HelpCenterGreetingProps {
	greeting?: string
	// Time-of-day labels, localized by the caller.
	greetings?: HelpCenterGreetings
	helpText?: string
	onSubmit?: (message: string) => boolean | void
	inputRef?: RefObject<HTMLInputElement | null>
	placeholder?: string
	className?: string
	headingClassName?: string
	// Questions under the field, dot-separated. Clicking one sends it.
	suggestions?: string[]
	onVoice?: () => void
	voiceLabel?: string
	// Defaults to the page accent.
	orbColors?: AgentOrbColors
}

export const HelpCenterGreeting = ({
	greeting,
	greetings = DEFAULT_GREETINGS,
	helpText = "How can I help?",
	onSubmit,
	inputRef,
	placeholder = "Ask anything...",
	className,
	headingClassName,
	suggestions = [],
	onVoice,
	voiceLabel,
	orbColors,
}: HelpCenterGreetingProps) => {
	const { morning, afternoon, evening } = greetings
	const fromClock = greeting === undefined
	// The reader's hour is unknown on the server, so the inline script below
	// fills the span while the HTML parses and hydration matches what it wrote.
	const [resolvedGreeting, setResolvedGreeting] = useState(() =>
		fromClock
			? typeof window === "undefined"
				? ""
				: pickGreeting(greetings)
			: greeting,
	)

	useEffect(() => {
		setResolvedGreeting(
			greeting ?? pickGreeting({ morning, afternoon, evening }),
		)
	}, [greeting, morning, afternoon, evening])

	return (
		<div className={cn("relative flex flex-col", className)}>
			<h1
				className={cn(
					"mb-5 mt-32 text-[clamp(1.25rem,5.8vw,2rem)] font-semibold leading-[1.15] tracking-tight sm:mt-12 sm:text-[2rem] lg:mt-[6.5rem]",
					headingClassName,
				)}
			>
				{fromClock ? (
					<>
						<span className="text-primary" data-greeting="">
							{resolvedGreeting ? `${resolvedGreeting}.` : ""}
						</span>
						<script
							dangerouslySetInnerHTML={{
								__html: preparePaintScript({ morning, afternoon, evening }),
							}}
						/>
					</>
				) : (
					resolvedGreeting && (
						<span className="text-primary">{`${resolvedGreeting}.`}</span>
					)
				)}
				<span className="ml-[0.25em] text-gray-500 dark:text-gray-400">
					{helpText}
				</span>
			</h1>
			{onSubmit && (
				<div className="mb-20 sm:mb-12">
					<div className={cn("rounded-[14px]", suggestions.length > 0 && TRAY)}>
						<AiChatInput
							onSubmit={onSubmit}
							inputRef={inputRef}
							variant="card"
							singleRow
							alwaysPrimarySubmit
							placeholder={placeholder}
							onVoice={onVoice}
							voiceLabel={voiceLabel}
							leading={<AgentOrb size={22} colors={orbColors} />}
							className={cn(FIELD, "rounded-[10px] [&_label]:rounded-[10px]")}
						/>
						{suggestions.length > 0 && (
							<div className="flex flex-col items-stretch pt-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-[7px] sm:px-2.5 sm:pb-1 sm:pt-2.5">
								{suggestions.map((suggestion, index) => (
									<Fragment key={suggestion}>
										{index > 0 && (
											<span
												aria-hidden
												className="hidden text-xs text-gray-300 sm:inline dark:text-gray-600"
											>
												·
											</span>
										)}
										<Button
											variant="link-muted"
											size="sm"
											onClick={() => onSubmit(suggestion)}
											wrapperClassName="contents"
											className={cn(
												QUESTION,
												// A phone row is a tap target, so it carries the rule that splits it from the one above.
												index > 0 &&
													"border-t border-black/[0.075] sm:border-t-0 dark:border-white/[0.06]",
											)}
										>
											<span className="truncate">{suggestion}</span>
										</Button>
									</Fragment>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
